import axios from 'axios';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from "react-router-dom";
import * as Yup from 'yup';
import { setAgent, setGlobalLoading, setUser } from '../../../actions/auth.actions';
import agentSession from '../../../utils/agentSession';
import { USER } from '../../../utils/constants';
import userSession from '../../../utils/userSession';
import Button from '../../components/Button';
import AgentAuthService from '../../services/agentAuth';
import AuthService from '../../services/auth';
import FormTemplate from '../Welcome/FormTemplate';
import { featureFlag } from '../../../utils/featureFlags';
import './SignIn.scss';

const authService = AuthService();
const agentAuthService = AgentAuthService();

const validationSchema = Yup.object().shape({
  email: Yup.string().email(),
  password: Yup.string(),
  'remember_me': Yup.boolean()
});

function SignIn({
  user = USER.INFLUENCERS,
  onAgentDetected = () => {}
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);
  const location = useLocation();
  const history = useHistory();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState(undefined);

  useEffect(() => {
    // If history has from
    let fromTmp = null;

    const searchParams = new URLSearchParams(location.search);

    if (get(history, 'location.state.from', null)) {
      fromTmp = history.location.state.from;
    } else if (get(location, 'state.from', null)) {
      // If current location has from on query params
      fromTmp = location.state.from;
    } else if (searchParams.get('from')) {
      fromTmp = searchParams.get('from');
    }

    if (/\/signin/.exec(fromTmp)) {
      fromTmp = null;
    }
    if (fromTmp) {
      setFrom(fromTmp);
    }

    if (auth.user && !auth.userReady) return;

    // If already signed in, redirect accordingly
    if (user === USER.ADMINS) {
      if (auth.user) {
        window.location.href = auth.user.admin ? '/manager/home' : '/influencers/';
        return;
      }
      if (auth.agent) {
        onAgentDetected();
        return;
      }
    }

    if (
      user === USER.INFLUENCERS && auth.user ||
      user === USER.CLIENTS && auth.agent
    ) {
      if (user === USER.INFLUENCERS && auth.user.admin) {
        window.location.href = '/manager/home';
        return;
      }
      if (fromTmp) {
        history.replace(fromTmp);
      } else {
        history.replace('/');
      }
    }
  }, [auth.userReady]);

  useEffect(() => {
    if (from && from !== '/' && user !== USER.ADMINS) {
      history.replace({
        pathname: user === USER.ADMINS ? '/team/signin' : '/signin',
        search: `?from=${from}`
      });
    }
  }, [from]);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      'remember_me': false
    },
    validationSchema,
    onSubmit: values => {
      setLoading(true);
      dispatch(setGlobalLoading(true));

      const handleError = (err) => {
        const status = get(err, 'response.status');
        setLoading(false);
        dispatch(setGlobalLoading(false));
        switch (status) {
          case 404:
            formik.setErrors({ email: t('common.user_not_found') });
            break;
          case 401:
            formik.setErrors({ password: t('common.user_or_password_incorrect') });
            break;
          case 500: default:
            formik.setErrors({ email: t('common.message_error') });
        }
      };

      const applyToken = (data) => {
        const token = data.auth_token;
        if (!token) throw { status: 500, message: 'No token' };
        axios.defaults.headers.common.Authorization = `Bearer null:${token}`;
      };

      if (user === USER.ADMINS) {
        authService.loginUser(values)
          .then((data) => {
            applyToken(data);
            return authService.getUser().then((userData) => {
              if (userData.force_password_change) {
                dispatch(setGlobalLoading(false));
                history.push('/team/set-password');
                return;
              }
              window.location.href = userData.admin ? '/manager/home' : '/influencers/';
            });
          })
          .catch((err) => {
            const status = get(err, 'response.status');
            const agentAccount = get(err, 'response.data.agent_account', false);

            if (status === 401 && agentAccount) {
              setLoading(false);
              dispatch(setGlobalLoading(false));
              onAgentDetected();
              return;
            }
            handleError(err);
          });
        return;
      }

      const loginPromise = user === USER.CLIENTS ?
        agentAuthService.loginAgent(values) :
        authService.loginUser(values);

      loginPromise
        .then((data) => {
          applyToken(data);

          const id = user === USER.CLIENTS ? agentSession('id') : userSession('id');

          const finallyFn = () => {
            if (user === USER.CLIENTS) {
              history.push({ pathname: '/' });
            }
            if (user === USER.INFLUENCERS) {
              if (userSession('admin')) {
                window.location.href = '/manager/home';
                return;
              }
              history.push({ pathname: from || '/' });
            }
            dispatch(setGlobalLoading(false));
          };

          if (!id) {
            const userPromise = user === USER.CLIENTS ? agentAuthService.getAgent() : authService.getUser();
            userPromise.then((userData) => {
              if (user === USER.CLIENTS) {
                dispatch(setAgent(userData));
              } else {
                dispatch(setUser(userData));
              }
              finallyFn();
            });
          } else {
            finallyFn();
          }
        })
        .catch(handleError);
    }
  });

  return <FormTemplate
    title={t('common.sign_in')}
    handleSubmit={formik.handleSubmit}
    submitText={t('common.sign_in')}
    loading={loading}
    user={user} >
    <Form.Group className='mb-3' >
      <Form.Label>
        {t('common.email')}
      </Form.Label>
      <Form.Control
        type='email'
        placeholder={t('common.text')}
        name="email"
        onChange={formik.handleChange}
        value={formik.values.email} />
      <Form.Text className="text-muted danger-text">
        {formik.touched.email && formik.errors.email}
      </Form.Text>
    </Form.Group>

    <Form.Group className='mb-3' controlId='formGroupPassword'>
      <Form.Label>
        {t('common.password')}
      </Form.Label>
      <InputGroup className="mb-3">
        <Form.Control
          type={showPassword ? 'text' : 'password'}
          placeholder={t('common.password')}
          name="password"
          onChange={formik.handleChange}
          value={formik.values.password} />
        <Button
          onClick={() => setShowPassword(!showPassword)}>
          <i className={classNames('fal', showPassword ? 'fa-eye-slash' : 'fa-eye')} />
        </Button>
      </InputGroup>
      <Form.Text className="text-muted danger-text">
        {formik.errors.password && t('common.invalid_password')}
      </Form.Text>
    </Form.Group>
    <Link to={user === USER.ADMINS ? '/team/forgot-your-password' : 'forgot-your-password'}>
      {t('common.pages.sign_in.forget_password')}
    </Link>
  </FormTemplate>;
}
SignIn.propTypes = {
  user: PropTypes.string,
  onAgentDetected: PropTypes.func,
};

export default SignIn;
